/**
 * Load Types - Matching B18 API response (LoadManagerNew)
 * Updated to support full load, stop, and dispatch details
 */

// Shared address interface
export interface AddressDetails {
  address?: string
  addressTwo?: string
  city?: string
  state?: string
  zipCode?: string
  zip?: string
  country?: string
  telephone?: string
  email?: string
  fax?: string
  ext?: string
  timezone?: string
}

// Distance metrics for tracking
export interface DistanceMetrics {
  travelledSoFar?: number
  travelledSinceLastUpdate?: number
  milesLeft?: number
  milesLeftTillNextDrop?: number
}

// Next stop metrics for ETA
export interface NextStopMetrics {
  milesLeft?: number
  timeRemaining?: string
  etaToNextStop?: string | number
  etaToNextStopTimestamp?: number
  etaToNextStopFormatted?: string
  etaToNextStopTimezone?: string
}

// Time metrics for tracking
export interface TimeMetrics {
  timeUtilizedSoFar?: string
  timeUsedSinceLastUpdate?: string
  timeRemaining?: string
}

// Timezone info
export interface Timezones {
  startAddressTimezone?: string
  currentAddressTimezone?: string
  destinationTimezone?: string
}

// Delivery status
export interface DeliveryStatus {
  status?: string
  statusColor?: string
  timeVarianceText?: string
}

// Truck position on route
export interface TruckPositionOnRoute {
  percentageCompleted?: number
  distanceCompleted?: number
}

// Segment progress
export interface SegmentProgress {
  percentageCompleted?: number
}

// Checkout ETA data
export interface CheckoutEtaData {
  currentLocation?: string
  current_time?: number | string
  distanceMetrics?: DistanceMetrics
  nextStopMetrics?: NextStopMetrics
  timeMetrics?: TimeMetrics
  timezones?: Timezones
  etaToDestination?: string
  etaToDestinationTimestamp?: number
  etaToDestinationTimezone?: string
  totalDistance?: number
  totalTime?: string
  etaFormatted?: string
  calculatedAt?: string
  deliveryStatus?: DeliveryStatus
  truckPositionOnRoute?: TruckPositionOnRoute
  segmentProgress?: SegmentProgress
  isTeam?: boolean
  breakTime?: number
}

// Last check call data
export interface LastCheckCall {
  _id?: string
  loadId?: string
  note?: string
  sender?: string
  current_address?: string
  current_time?: number
  current_latlng?: [number, number]
  stopNumber?: number | null
  facilityAddress?: string
  calculated_data?: CheckoutEtaData
  nextStopMetrics?: NextStopMetrics
  invalid?: boolean
  reason?: string
  invalidatedAt?: string | null
}

// Check call container
export interface CheckCallData {
  lastCheckCall?: LastCheckCall
  checkoutEtaData?: CheckoutEtaData
}

// Main Load interface
export interface LoadDetails {
  _id: string
  loadNumber: string | number
  status: string
  createdAt?: string
  updatedAt?: string

  // Check call / Tracking data
  checkCall?: CheckCallData

  // Customer details (expanded to match API)
  customer?: {
    customerDetails?: {
      _id?: string
      customerId?: string
      brokerName?: string
      dbaName?: string
      mcNumber?: string
      customerLoadNumber?: string
      isActive?: boolean
    }
    branch?: {
      branchId?: string
      name?: string
      addressDetails?: AddressDetails
      billingAddressDetails?: AddressDetails
    }
  }

  // Legacy customer fields (for backward compatibility)
  customerLoad?: string

  // Contact information
  contact?: {
    contactId?: string
    name?: string
    email?: string
    telephone?: string
    title?: string
  }

  // Freight details
  freightDetails?: {
    freightType?: string
    commodity?: string
    isAsPerBOL?: boolean
    temperature?: {
      min?: number
      max?: number
    }
    temperatureType?: number
    cycle?: string
    pulp?: string
  }

  // Equipment requirements
  requirements?: {
    team?: string
    trailerSize?: string
    trailerType?: string
  }

  // Rate details
  rateDetails?: {
    rate?: string
    type?: number
    description?: string
    additionalCharges?: {
      type?: string
      amount?: string
      description?: string
    }[]
  }

  // Sales person details
  salesPersonDetails?: {
    userId?: string
    name?: string
    email?: string
  }

  // Carrier (load level)
  carrier?: {
    carrierDetails?: {
      _id?: string
      carrierId?: string
      carrierName?: string
      mcNumber?: string
      dotNumber?: string
      isActive?: boolean
    }
    branch?: {
      _id?: string
      branchId?: string
      name?: string
      addressDetails?: AddressDetails
    }
  }

  // Stops
  stops?: LoadStop[]
  emptyMoveStops?: LoadStop[]

  // Packages (load level)
  packages?: LoadPackage[]

  // Dispatches
  dispatches?: LoadDispatch[]

  // Notes
  loadNote?: string
}

// Stop types matching main app
export type StopType =
  | 'pickup'
  | 'drop'
  | 'transit-drop'
  | 'transit-pickup'
  | 'empty-move'

// Stop interface (expanded)
export interface LoadStop {
  _id?: string
  uid?: string
  index?: number
  loadId?: string
  dispatchId?: string

  // Stop type
  stopType?: StopType
  type?: string // legacy field

  // Status
  status?: string

  // Facility details
  facilityId?: string
  facilityDetails?: {
    _id?: string
    name?: string
    addressDetails?: AddressDetails
    hours?: FacilityHours[]
    timezone?: string
  }

  // City/State address (when facility hidden or not selected)
  cityStateAddressDetails?: {
    address?: string
    city?: string
    state?: string
    country?: string
    timezone?: string
    zip?: string
    formattedAddress?: string
  }
  hideAddress?: boolean

  // Schedule
  scheduleType?: string // 'FCFS' | 'ByAppointment'
  scheduleStart?: { seconds?: number | null; scheduled?: boolean }
  scheduleEnd?: { seconds?: number | null; scheduled?: boolean }
  // P2-1243: with 2+ options and none active, the schedule above is cleared
  appointmentOptions?: { active?: boolean }[]

  // Check-in/out (Unix timestamps)
  checkIn?: number | null
  checkOut?: number | null
  checkedIn?: number
  checkedOut?: number

  // Notes
  facilityNotes?: string

  // Packages
  packages?: { eventType?: string; uid?: string }[]

  // Transit stop specific
  isTransitStop?: boolean
  transitType?: 'drop' | 'pickup'
  transitNotes?: string
  linkedTransitStopId?: string

  // Empty move specific
  isEmptyMoveStop?: boolean
  systemGenerated?: boolean
  emptyMoveType?: 'driver' | 'truck' | 'trailer'
  emptyMoveSerialNumber?: string
  emptyMoveLocationData?: {
    address?: string
    location?: { latitude?: number; longitude?: number }
    timezone?: string
    source?: string
    driverStatus?: string
    hideAddress?: boolean
    departureTime?: number
    notes?: string
  }

  // ETS route metrics — distanceValue is meters FROM this stop TO the next
  // stop in the load's overall route.
  ets_details?: {
    distance?: string
    distanceValue?: number | string
    duration?: string
    durationValue?: number
  }
  // P2-1300: direct distance to the next stop WITHIN the same dispatch
  // (segregated view). null on a dispatch's last stop / empty-move stops.
  dispatch_ets_details?: {
    distance?: string
    distanceValue?: number | string
    duration?: string
    durationValue?: number
  } | null

  // Legacy fields (for backward compatibility)
  sequence?: number
  appointmentStart?: string
  appointmentEnd?: string
  actualArrival?: string
  actualDeparture?: string
}

// Facility hours
export interface FacilityHours {
  days?: string[]
  from?: string
  to?: string
}

// Package interface
export interface LoadPackage {
  _id?: string
  uid?: string
  packageId?: string
  unitType?: string
  count?: number
  weight?: number | string
  length?: number
  width?: number
  height?: number
  pickupNote?: string
  dropNote?: string
  note?: string
}

// Dispatch interface (expanded)
export interface LoadDispatch {
  _id?: string
  dispatchId?: string
  stops?: string[]
  status?: string

  // Carrier details
  carrier?: {
    carrierDetails?: {
      _id?: string
      carrierId?: string
      carrierName?: string
      mcNumber?: string
      dotNumber?: string
      isActive?: boolean
    }
    branch?: {
      _id?: string
      branchId?: string
      name?: string
      addressDetails?: AddressDetails
      billingAddressDetails?: AddressDetails
    }
  }

  // Driver details
  driverDetails?: {
    _id?: string
    b18Id?: string
    firstName?: string
    lastName?: string
    mobileNumber?: string
    isActive?: boolean
    addressDetails?: {
      telephone?: string
      email?: string
      address?: string
      city?: string
      state?: string
      zipCode?: string
      country?: string
    }
  }

  // Extra drivers (team driving)
  extraDrivers?: Array<{
    _id?: string
    b18Id?: string
    firstName?: string
    lastName?: string
    mobileNumber?: string
    isActive?: boolean
    addressDetails?: {
      telephone?: string
      email?: string
    }
  }>

  // Equipment
  truckDetails?: {
    _id?: string
    truckNumber?: string
    make?: string
    model?: string
    year?: string
  }
  trailerDetails?: {
    _id?: string
    trailerNumber?: string
    companyName?: string
    make?: string
    model?: string
    year?: string
    notes?: string
  }

  // Empty move dispatch
  isEmptyMoveDispatch?: boolean
  // Links an EM-only dispatch to its parent main dispatch (P2-1166).
  linkedMainDispatchId?: string
  driverStartLocation?: {
    lat?: number
    lng?: number
    address?: string
    city?: string
    state?: string
    timezone?: string
    capturedAt?: number
    // P2-1166 additions — populated by BE on every non-continuation dispatch.
    source?:
      | 'user-input'
      | 'location-tracking'
      | 'check-call'
      | 'stop-activity'
      | 'previous-dispatch-end'
      | 'none'
    isEditable?: boolean
    isEdited?: boolean
    note?: string
  }

  // P2-1166 — frozen at dispatch creation; never recomputed.
  // When true, the same driver continues from the prior dispatch (no empty move).
  isContinuationOfPreviousDriver?: boolean
  linkedPreviousDispatchId?: string | null

  // Carry over data (for transit stops)
  carryOverData?: {
    carrier?: boolean
    driver?: boolean
    truck?: boolean
    trailer?: boolean
    fromDispatchId?: string
  }

  // Legacy fields (for backward compatibility)
  driver?: {
    _id?: string
    name?: string
    phone?: string
  }
  truck?: {
    _id?: string
    number?: string
  }
  trailer?: {
    _id?: string
    number?: string
  }
}

// Hook return type
export interface UseLoadDetailsReturn {
  load: LoadDetails | null
  loading: boolean
  error: Error | null
  refetch: () => void
}

// Status color mapping
export const STATUS_COLORS: Record<
  string,
  'default' | 'primary' | 'secondary' | 'error' | 'info' | 'success' | 'warning'
> = {
  draft: 'default',
  quoted: 'info',
  booked: 'primary',
  dispatched: 'secondary',
  'in-transit': 'warning',
  delivered: 'success',
  completed: 'success',
  cancelled: 'error',
  issue: 'error'
}

/**
 * Get MUI color for load status
 */
export const getStatusColor = (
  status?: string
):
  | 'default'
  | 'primary'
  | 'secondary'
  | 'error'
  | 'info'
  | 'success'
  | 'warning' => {
  if (!status) return 'default'
  return STATUS_COLORS[status.toLowerCase()] || 'default'
}
